import { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '../ui/sheet'
import { LogOut, User, BookOpen, Library, Home, Shield, Menu, Wallet, Crown, Sparkles, TrendingUp, CreditCard, FlaskConical, HelpCircle, Gift, Receipt } from 'lucide-react'
import { User as UserType, MembershipTier } from '../../types'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { LanguageSwitcher } from '../LanguageSwitcher'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../../lib/api/client'
import { RewardClaimWidget } from '../rewards/RewardClaimWidget'
import { CreditPurchaseDialog } from '../membership/CreditPurchaseDialog'
import { MembershipUpgradeDialog } from '../membership/MembershipUpgradeDialog'
import { walletEvents } from '../../lib/events/wallet-events'
import { useServerConfig } from '../../contexts/ServerConfigContext'

interface HomeHeaderProps {
  user: UserType | null
  onLogout: () => void
}

interface MembershipInfo {
  tier: MembershipTier
  tier_name: string
  credit_balance_usd: number
}

// Get tier badge styles
function getTierBorderColor(tier: MembershipTier): string {
  switch (tier) {
    case 'book_giant':
      return 'ring-2 ring-yellow-500 ring-offset-2 ring-offset-background'
    case 'plus':
      return 'ring-2 ring-purple-500 ring-offset-2 ring-offset-background'
    default:
      return 'ring-2 ring-blue-500 ring-offset-2 ring-offset-background'
  }
}

function getTierIcon(tier: MembershipTier) {
  switch (tier) {
    case 'book_giant':
      return <Crown className="h-3 w-3 text-yellow-500" />
    case 'plus':
      return <Sparkles className="h-3 w-3 text-purple-500" />
    default:
      return <TrendingUp className="h-3 w-3 text-blue-500" />
  }
}

export function HomeHeader({ user, onLogout }: HomeHeaderProps) {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const location = useLocation()
  const { config: serverConfig } = useServerConfig()
  const [membership, setMembership] = useState<MembershipInfo | null>(null)
  const [showCreditPurchase, setShowCreditPurchase] = useState(false)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  
  // Logo styling based on server mode
  const isTestMode = serverConfig.isTest
  const logoColorClass = isTestMode ? 'text-orange-500' : 'text-primary'

  useEffect(() => {
    if (user) {
      loadMembership()
    }
  }, [user])

  // Subscribe to wallet balance update events
  useEffect(() => {
    const unsubscribe = walletEvents.subscribe((newBalance) => {
      if (newBalance !== undefined) {
        // Direct balance update
        setMembership(prev => prev ? { ...prev, credit_balance_usd: newBalance } : null)
      } else {
        // No balance provided, refetch membership data
        loadMembership()
      }
    })
    return unsubscribe
  }, [])

  const loadMembership = async () => {
    try {
      const response = await apiClient.membership.getMembership()
      if (response.success) {
        setMembership({
          tier: response.membership.tier,
          tier_name: response.membership.tier_name || response.membership.tier,
          credit_balance_usd: response.membership.credit_balance_usd,
        })
      }
    } catch (error) {
      console.error('Error loading membership:', error)
    }
  }

  const handleBalanceUpdate = (newBalance: number) => {
    setMembership(prev => prev ? { ...prev, credit_balance_usd: newBalance } : null)
  }

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  // Get FAQ URL based on current language (static pages for SEO)
  const getFaqUrl = () => {
    const lang = i18n.language
    const langPrefix: Record<string, string> = {
      'de': '/de',
      'es': '/es',
      'uk': '/uk',
      'nb': '/nb',
    }
    return `${langPrefix[lang] || ''}/how-it-works/`
  }

  const navigationItems = [
    { path: '/', label: t('navigation.home'), icon: Home },
    { path: '/library', label: t('navigation.library'), icon: Library },
    { path: '/my-books', label: t('navigation.myBooks'), icon: BookOpen },
  ]

  return (
    <>
      <header className="site-header border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo and Navigation */}
            <div className="flex items-center space-x-4 md:space-x-8">
              <div 
                className="logo flex items-center space-x-2 cursor-pointer"
                onClick={() => navigate('/')}
                title={isTestMode ? 'Test Environment' : undefined}
              >
                {isTestMode ? (
                  <FlaskConical className={`h-8 w-8 ${logoColorClass}`} />
                ) : (
                  <BookOpen className={`h-8 w-8 ${logoColorClass}`} />
                )}
                <h1 className={`text-xl md:text-2xl font-bold ${logoColorClass}`}>{t('app.name')}</h1>
              </div>
              
              {/* Desktop Navigation */}
              <nav className="desktop-nav hidden md:flex items-center space-x-6">
                {navigationItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`nav-link flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive(item.path)
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </Link>
                  )
                })}
                {/* FAQ - Static page link (external for SEO) */}
                <a
                  href={getFaqUrl()}
                  className="faq-link flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <HelpCircle className="h-4 w-4" />
                  <span>{t('navigation.howItWorks', 'How It Works')}</span>
                </a>
              </nav>
            </div>
            
            {/* User Menu and Mobile Navigation */}
            <div className="flex items-center space-x-4">
              {/* Language Switcher */}
              <LanguageSwitcher />
              
              {/* Desktop Reward Claim Widget - Only visible when user has rewards */}
              {user && (
                <div className="hidden md:block">
                  <RewardClaimWidget 
                    mode="desktop-header" 
                    onBalanceUpdate={handleBalanceUpdate} 
                  />
                </div>
              )}
              
              {/* Admin Button - Only visible for admins */}
              {user && user.role === 'admin' && (
                <Link
                  to="/admin"
                  className={`admin-link inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-9 px-3 ${
                    isActive('/admin')
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <Shield className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('navigation.admin')}</span>
                </Link>
              )}
              
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="user-menu-trigger relative h-10 w-10 rounded-full p-0">
                      <Avatar className={`h-10 w-10 ${membership ? getTierBorderColor(membership.tier) : ''}`}>
                        <AvatarImage src={user.image || undefined} alt={user.name || 'User'} />
                        <AvatarFallback>
                          {user.name ? user.name.charAt(0).toUpperCase() : <User className="h-4 w-4" />}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="user-menu-dropdown w-64" align="end" forceMount>
                    {/* User Info Section */}
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{user.name}</p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {user.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    
                    {/* Membership & Wallet Section */}
                    {membership && (
                      <>
                        <div className="membership-info px-2 py-2">
                          {/* Current Plan */}
                          <div 
                            className="plan-row flex items-center justify-between mb-2 rounded p-1 -mx-1"
                          >
                            <div 
                              className="flex items-center gap-2 cursor-pointer hover:bg-muted rounded px-1 -mx-1"
                              onClick={() => navigate('/profile')}
                            >
                              {getTierIcon(membership.tier)}
                              <span className="text-sm font-medium">{membership.tier_name}</span>
                            </div>
                            {membership.tier !== 'book_giant' && (
                              <span 
                                className="text-xs text-primary hover:underline cursor-pointer"
                                onClick={() => setShowUpgradeDialog(true)}
                              >
                                {t('membership.upgrade')}
                              </span>
                            )}
                          </div>
                          
                          {/* Wallet Balance */}
                          <div 
                            className="wallet-row flex items-center justify-between cursor-pointer hover:bg-muted rounded p-1 -mx-1"
                            onClick={() => setShowCreditPurchase(true)}
                          >
                            <div className="flex items-center gap-2">
                              <Wallet className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">{t('rewards.walletBalance')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-primary">
                                ${membership.credit_balance_usd.toFixed(2)}
                              </span>
                              <CreditCard className="h-3 w-3 text-primary" />
                            </div>
                          </div>
                        </div>
                        <DropdownMenuSeparator />
                      </>
                    )}
                    
                    <DropdownMenuItem onClick={() => navigate('/profile')}>
                      <User className="mr-2 h-4 w-4" />
                      <span>{t('navigation.profile')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/referrals')}>
                      <Gift className="mr-2 h-4 w-4" />
                      <span>{t('navigation.referrals', 'Referrals')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/transactions')}>
                      <Receipt className="mr-2 h-4 w-4" />
                      <span>{t('navigation.transactions', 'Transactions')}</span>
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/admin')}>
                          <Shield className="mr-2 h-4 w-4" />
                          <span>{t('admin.dashboard')}</span>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>{t('navigation.logout')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  onClick={() => navigate('/login')}
                >
                  {t('navigation.login')}
                </Button>
              )}

              {/* Mobile Navigation Menu - Rightmost */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="mobile-menu-trigger md:hidden">
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Open menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[300px] sm:w-[400px]">
                  <SheetHeader>
                    <SheetTitle>{t('navigation.home')}</SheetTitle>
                  </SheetHeader>
                  <nav className="flex flex-col space-y-2 mt-6">
                    {navigationItems.map((item) => {
                      const Icon = item.icon
                      return (
                        <SheetClose asChild key={item.path}>
                          <Link
                            to={item.path}
                            className={`mobile-nav-link flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors w-full ${
                              isActive(item.path)
                                ? 'bg-primary text-primary-foreground'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                            }`}
                          >
                            <Icon className="h-5 w-5" />
                            <span>{item.label}</span>
                          </Link>
                        </SheetClose>
                      )
                    })}
                    {/* FAQ - Static page link (external for SEO) */}
                    <SheetClose asChild>
                      <a
                        href={getFaqUrl()}
                        className="mobile-faq-link flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors w-full text-muted-foreground hover:text-foreground hover:bg-muted"
                      >
                        <HelpCircle className="h-5 w-5" />
                        <span>{t('navigation.howItWorks', 'How It Works')}</span>
                      </a>
                    </SheetClose>
                    {user && user.role === 'admin' && (
                      <SheetClose asChild>
                        <Link
                          to="/admin"
                          className={`mobile-nav-link flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors w-full ${
                            isActive('/admin')
                              ? 'bg-primary text-primary-foreground'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                          }`}
                        >
                          <Shield className="h-5 w-5" />
                          <span>{t('navigation.admin')}</span>
                        </Link>
                      </SheetClose>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Credit Purchase Dialog */}
      <CreditPurchaseDialog
        open={showCreditPurchase}
        onOpenChange={setShowCreditPurchase}
        onPurchaseComplete={loadMembership}
      />

      {/* Membership Upgrade Dialog */}
      {showUpgradeDialog && membership && (
        <MembershipUpgradeDialog
          currentTier={membership.tier}
          onClose={() => setShowUpgradeDialog(false)}
          onUpgraded={() => {
            loadMembership()
            setShowUpgradeDialog(false)
          }}
        />
      )}
    </>
  )
}
